<template>
  <div class="expense-form">
    <van-field v-model="form.title" label="名稱" placeholder="例如：晚餐" required />
    <div class="field-row">
      <van-field v-model="form.amount" label="金額" type="number" placeholder="0" />
      <van-field label="幣別">
        <template #input>
          <select v-model="form.currency" class="native-select">
            <option v-for="c in currencies" :key="c" :value="c">{{ c }}</option>
          </select>
        </template>
      </van-field>
    </div>
    <p class="muted hint" v-if="form.currency !== settlementCurrency">
      將以 1 {{ form.currency }} = {{ rate }} {{ settlementCurrency }} 換算（可在財務設定調整匯率）
    </p>

    <MemberSelect v-model="form.payerId" :trip-id="tripId" label="付款人" allow-add-placeholder />

    <van-field label="類別">
      <template #input>
        <select v-model="form.category" class="native-select">
          <option v-for="c in categories" :key="c" :value="c">{{ c }}</option>
        </select>
      </template>
    </van-field>

    <van-field v-model="form.occurredAt" label="時間" type="datetime-local" />

    <div class="section-title" style="margin-top: 10px;">分攤方式</div>
    <div class="split-mode">
      <button type="button" class="v-btn" :class="{ active: form.splitMode === 'even' }" @click="form.splitMode = 'even'">平均分攤</button>
      <button type="button" class="v-btn" :class="{ active: form.splitMode === 'custom' }" @click="form.splitMode = 'custom'">逐人指定</button>
      <button type="button" class="v-btn" :class="{ active: form.splitMode === 'none' }" @click="form.splitMode = 'none'">個人支出</button>
    </div>

    <template v-if="form.splitMode === 'even'">
      <MemberMultiSelect v-model="form.memberIds" :members="members" />
    </template>

    <template v-else-if="form.splitMode === 'custom'">
      <div v-for="m in members" :key="m.id" class="custom-row">
        <MemberAvatar :member="m" :size="20" />
        <span class="name">{{ m.name }}</span>
        <input
          type="number" class="amount-input" placeholder="0"
          :value="customAmounts[m.id] || ''"
          @input="customAmounts = { ...customAmounts, [m.id]: $event.target.value }"
        >
      </div>
      <p class="muted hint" :class="{ danger: !customValid }">
        已分配 {{ customTotal }} / 總金額 {{ form.amount || 0 }}
        <span v-if="!customValid">，兩者需相等才能儲存</span>
      </p>
    </template>

    <template v-else>
      <p class="muted hint">個人支出不參與分帳，可見性固定為「只有我」，只計入自己的花費統計。</p>
    </template>

    <van-field v-model="form.note" label="備註" type="textarea" rows="2" placeholder="選填" />

    <van-checkbox v-model="form.pending" shape="square" class="pending-checkbox">
      先拍收據，之後再補齊資料（狀態標記為待補齊）
    </van-checkbox>

    <div class="actions">
      <van-button block type="default" @click="$emit('cancel')">取消</van-button>
      <van-button block type="primary" :disabled="!canSubmit" @click="submit">儲存支出</van-button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import dayjs from 'dayjs'
import MemberSelect from '@/components/common/MemberSelect.vue'
import MemberMultiSelect from '@/components/common/MemberMultiSelect.vue'
import MemberAvatar from '@/components/common/MemberAvatar.vue'

const CATEGORIES = ['機票', '住宿', '餐飲', '交通', '購物', '門票', '其他']
const CURRENCIES = ['HKD', 'JPY', 'TWD', 'USD', 'CNY', 'EUR']

export default {
  name: 'ExpenseForm',
  components: { MemberSelect, MemberMultiSelect, MemberAvatar },
  props: {
    tripId: { type: String, required: true },
    creatorId: { type: String, required: true },
    presetPayerId: { type: String, default: null }
  },
  data () {
    return {
      categories: CATEGORIES,
      currencies: CURRENCIES,
      customAmounts: {},
      form: {
        title: '',
        amount: '',
        currency: '',
        payerId: this.presetPayerId,
        category: '餐飲',
        occurredAt: dayjs().format('YYYY-MM-DDTHH:mm'),
        splitMode: 'even',
        memberIds: [],
        pending: false,
        note: ''
      }
    }
  },
  computed: {
    ...mapGetters({
      membersByTrip: 'members/byTrip', tripById: 'trip/byId', rateFor: 'finance/rateFor'
    }),
    members () {
      return this.membersByTrip(this.tripId)
    },
    trip () {
      return this.tripById(this.tripId)
    },
    settlementCurrency () {
      return this.trip ? this.trip.settlementCurrency : 'HKD'
    },
    rate () {
      return this.rateFor(this.tripId, this.form.currency)
    },
    customTotal () {
      return Object.values(this.customAmounts).reduce((s, v) => s + (Number(v) || 0), 0)
    },
    customValid () {
      return Math.abs(this.customTotal - Number(this.form.amount || 0)) < 0.01
    },
    canSubmit () {
      if (!this.form.title || !this.form.amount || !this.form.payerId) return false
      if (this.form.splitMode === 'even' && !this.form.memberIds.length) return false
      if (this.form.splitMode === 'custom' && !this.customValid) return false
      return true
    }
  },
  created () {
    this.form.currency = this.settlementCurrency
    this.form.memberIds = this.members.map(m => m.id)
  },
  methods: {
    ...mapActions({ createExpense: 'finance/createExpense' }),
    submit () {
      if (!this.canSubmit) return
      const customShares = Object.entries(this.customAmounts)
        .filter(([, v]) => Number(v) > 0)
        .map(([memberId, amount]) => ({ memberId, amount: Number(amount) }))
      this.createExpense({
        tripId: this.tripId,
        title: this.form.title,
        amount: Number(this.form.amount),
        currency: this.form.currency,
        settlementCurrency: this.settlementCurrency,
        payerId: this.form.payerId,
        category: this.form.category,
        occurredAt: new Date(this.form.occurredAt).toISOString(),
        splitMode: this.form.splitMode,
        memberIds: this.form.splitMode === 'even' ? this.form.memberIds : [],
        customShares,
        pending: this.form.pending,
        note: this.form.note,
        createdBy: this.creatorId
      })
      this.$emit('done')
    }
  }
}
</script>

<style scoped>
.field-row { display: flex; }
.field-row >>> .van-cell { flex: 1; }
.native-select { border: none; font-size: 14px; width: 100%; background: transparent; color: var(--color-text); }
.hint { padding: 4px 16px 8px; }
.hint.danger { color: var(--color-danger); }
.split-mode { display: flex; gap: 8px; padding: 0 16px 10px; }
.v-btn {
  flex: 1; padding: 8px 4px; border-radius: var(--radius-md);
  border: 1px solid var(--color-border); background: #fff; font-size: 12px; color: var(--color-text-dim);
}
.v-btn.active { border-color: var(--color-primary); background: #fff5f0; color: var(--color-primary-dark); font-weight: 600; }
.custom-row { display: flex; align-items: center; gap: 8px; padding: 6px 16px; }
.custom-row .name { flex: 1; font-size: 13px; }
.amount-input { width: 90px; border: 1px solid var(--color-border); border-radius: 8px; padding: 6px 8px; font-size: 13px; }
.pending-checkbox { padding: 10px 16px; font-size: 12px; }
.actions { display: flex; gap: 10px; padding: 16px; }
</style>
